/**
 * How a physician's script is counted and who may write one. The numbers and the state list are
 * engineering defaults for unanswered questions: Q-007 (refills and how long a script runs) and
 * Q-006 (which states at launch).
 *
 * Release to the pharmacy is payment (CLI-030), a different bead. This module never marks a script
 * released.
 */
import type { ScriptState } from "./machines.ts";

/**
 * Q-007 is unanswered. The founders said about six months. Until counsel names the legal limit,
 * a script is written with 5 refills and 180 days — the usual reading of a 30-day fill plus five
 * refills. Each monthly order spending a refill is CLI-040, owned by renewals.
 */
export const SCRIPT_REFILLS_TOTAL = 5;

export const SCRIPT_VALIDITY_DAYS = 180;

/** ACC-025 / MSG-021: the account and email fire this many days before the last signed pouch. */
export const REFILL_WARNING_DAYS = 30;

/**
 * Q-006 is unanswered (which states at launch). Until counsel names the list, Lattice treats every
 * US state and DC as a launch state. Narrowing this array is the only change Q-006 should need.
 */
export const LAUNCH_STATES = [
  "AK",
  "AL",
  "AR",
  "AZ",
  "CA",
  "CO",
  "CT",
  "DC",
  "DE",
  "FL",
  "GA",
  "HI",
  "IA",
  "ID",
  "IL",
  "IN",
  "KS",
  "KY",
  "LA",
  "MA",
  "MD",
  "ME",
  "MI",
  "MN",
  "MO",
  "MS",
  "MT",
  "NC",
  "ND",
  "NE",
  "NH",
  "NJ",
  "NM",
  "NV",
  "NY",
  "OH",
  "OK",
  "OR",
  "PA",
  "RI",
  "SC",
  "SD",
  "TN",
  "TX",
  "UT",
  "VA",
  "VT",
  "WA",
  "WI",
  "WV",
  "WY",
] as const;

const LAUNCH_STATE_SET: ReadonlySet<string> = new Set(LAUNCH_STATES);

const ISO_DAY = /^(\d{4}-\d{2}-\d{2})/;

/** The calendar day an ISO timestamp falls on, UTC. */
function dayOf(iso: string): string {
  const day = ISO_DAY.exec(iso)?.[1];

  if (day === undefined) throw new Error(`not an ISO date: ${iso}`);

  return day;
}

export function isLaunchState(state: string): boolean {
  return LAUNCH_STATE_SET.has(state);
}

/**
 * CLI-050. A physician with no recorded licences is treated as licensed in every launch state
 * (Andrea at launch, Q-008). A patient with no recorded state is visible only under that default,
 * because shipping address is collected at checkout (ONB-046), after review. An explicit licence
 * list never sees an unknown-state patient, and never sees a state that is not both licensed and
 * in LAUNCH_STATES.
 */
export function physicianMayReview(
  patientState: string | null,
  licensedStates: readonly string[],
): boolean {
  const licences =
    licensedStates.length === 0 ? LAUNCH_STATES : licensedStates.filter(isLaunchState);

  if (licences.length === 0) return false;

  if (patientState === null) return licensedStates.length === 0;

  return licences.some((state) => state === patientState);
}

/** A staff physician's licence in one state. Only a verified one lets them review there. */
export interface PhysicianLicence {
  readonly state: string;
  readonly verified: boolean;
}

/**
 * AUTH-012. `physicianMayReview` for a physician whose licences are recorded. Recording any
 * licence ends the launch default, so a physician with only unverified licences reviews nothing
 * until an admin verifies one.
 */
export function physicianMayReviewWith(
  patientState: string | null,
  licences: readonly PhysicianLicence[],
): boolean {
  if (licences.length === 0) return physicianMayReview(patientState, []);

  const verified = licences.flatMap((l) => (l.verified ? [l.state] : []));

  return verified.length > 0 && physicianMayReview(patientState, verified);
}

/** `expires_on` as YYYY-MM-DD, SCRIPT_VALIDITY_DAYS after the draft's UTC calendar day. */
export function scriptExpiresOn(draftedAt: string, days: number = SCRIPT_VALIDITY_DAYS): string {
  const start = new Date(`${dayOf(draftedAt)}T00:00:00Z`);

  start.setUTCDate(start.getUTCDate() + days);

  return start.toISOString().slice(0, 10);
}

export function scriptIsExpired(expiresOn: string, today: string): boolean {
  return dayOf(expiresOn) < dayOf(today);
}

export function refillsRemaining(refillsTotal: number, refillsUsed: number): number {
  return Math.max(0, refillsTotal - refillsUsed);
}

/**
 * CLI-040: the next monthly order is held when the script cannot cover it — voided, exhausted,
 * expired, past `expires_on`, or out of refills.
 */
export function nextOrderHeld(
  script: {
    readonly status: ScriptState;
    readonly refillsTotal: number;
    readonly refillsUsed: number;
    readonly expiresOn: string;
  },
  today: string,
): boolean {
  if (script.status === "voided" || script.status === "exhausted" || script.status === "expired")
    return true;

  return (
    scriptIsExpired(script.expiresOn, today) ||
    refillsRemaining(script.refillsTotal, script.refillsUsed) === 0
  );
}

/** Advance a YYYY-MM-DD (or ISO timestamp) by whole UTC months, keeping the billing day. */
export function addUtcMonths(day: string, months: number): string {
  const start = new Date(`${dayOf(day)}T00:00:00Z`);

  start.setUTCMonth(start.getUTCMonth() + months);

  return start.toISOString().slice(0, 10);
}

export function addUtcDays(day: string, days: number): string {
  const start = new Date(`${dayOf(day)}T00:00:00Z`);

  start.setUTCDate(start.getUTCDate() + days);

  return start.toISOString().slice(0, 10);
}

/** The ship date of the last pouch this script still covers, or null when none remain. */
export function lastPouchOn(nextShipOn: string, remaining: number): string | null {
  if (remaining <= 0) return null;

  return addUtcMonths(nextShipOn, remaining - 1);
}

/** ACC-025 / MSG-021: true from 30 days before the last pouch through that pouch's day. */
export function refillWarningDue(
  lastOn: string,
  today: string,
  days: number = REFILL_WARNING_DAYS,
): boolean {
  const warnOn = addUtcDays(lastOn, -days);

  return warnOn <= dayOf(today) && dayOf(today) <= dayOf(lastOn);
}
